import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Lock, Loader2, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SectorPicker } from "@/components/ui/SectorPicker";
import { FileUploadSection } from "@/components/onboarding/shared/FileUploadSection";
import { PaywallDialog } from "@/components/PaywallDialog";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useNotificationGuideProgress } from "@/hooks/api/query/useNotificationGuideProgress";
import { useGetDecizieInregistrare } from "@/hooks/api/query/useGetDecizieInregistrare";
import { useSaveDecizieInregistrare } from "@/hooks/api/mutate/useSaveDecizieInregistrare";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { useAuth } from "@/contexts/AuthContext";
import { extractDataFromFiles } from "@/lib/file-extraction";
import { getNotificationPdfStatus } from "@/utils/guideProgressCalculator";
import {
  mapDecisionToInput,
  validateDecizieFields,
} from "@/utils/decizieInregistrareUtils";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { OnboardingFooter } from "@/components/onboarding/shared/OnboardingFooter";
import { cleanCui } from "@/utils/cui-utils";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import type { DecizieInregistrareInput } from "@/types/decizieInregistrare";

function FieldError({
  fieldName,
  errors,
}: {
  fieldName: string;
  errors: Record<string, string>;
}) {
  const error = errors[fieldName];
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 mt-1">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

type FormState = {
  decision_number: string;
  decision_date: string;
  entity_name: string;
  address: string;
  county: string;
  cui: string;
  category: string;
  category_code: string;
  registration_number: string;
  sectors: string[];
};

function toFormState(input: Partial<DecizieInregistrareInput>): FormState {
  return {
    decision_number: input.decision_number ?? "",
    decision_date: input.decision_date ?? "",
    entity_name: input.entity_name ?? "",
    address: input.address ?? "",
    county: input.county ?? "",
    cui: input.cui ?? "",
    category: input.category ?? "",
    category_code: input.category_code ?? "",
    registration_number: input.registration_number ?? "",
    sectors: input.sectors ?? [],
  };
}

function toDecizieInput(
  sessionId: string,
  form: FormState
): DecizieInregistrareInput {
  return {
    session_id: sessionId,
    decision_number: form.decision_number || null,
    decision_date: form.decision_date || null,
    entity_name: form.entity_name || null,
    address: form.address || null,
    county: form.county || null,
    cui: form.cui || null,
    category: form.category || null,
    category_code: form.category_code || null,
    registration_number: form.registration_number || null,
    sectors: form.sectors,
  };
}

export default function DecizieInregistrare() {
  const navigate = useNavigate();
  const { currentSessionId } = useSessionSwitcher();
  const { toast } = useToast();
  const { user } = useAuth();

  const {
    steps,
    isLoading: isLoadingProgress,
    entityData,
  } = useNotificationGuideProgress(currentSessionId);
  const decizieQuery = useGetDecizieInregistrare(currentSessionId);
  const saveMutation = useSaveDecizieInregistrare();
  const { mutateAsync: deductCredits } = useDeductCredits();

  const { data: creditCheck } = useCheckActionCredits(
    {
      session_id: currentSessionId || "",
      action_type: CREDIT_ACTION_TYPES.EVIDENTA_DECIZIE_INREGISTRARE_DNSC,
    },
    !!currentSessionId
  );

  const [form, setForm] = useState<FormState>(toFormState({}));
  const [isExtracting, setIsExtracting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formInitialized, setFormInitialized] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  if (
    !isLoadingProgress &&
    !decizieQuery.isLoading &&
    !formInitialized &&
    decizieQuery.data
  ) {
    setForm(toFormState(decizieQuery.data));
    setFormInitialized(true);
  }

  const isLoading = isLoadingProgress || decizieQuery.isLoading;
  const notificareCompleted =
    !isLoading && getNotificationPdfStatus(entityData) === "completed";

  const sessionCui = cleanCui(entityData?.cui ?? "");
  const formCui = cleanCui(form.cui);
  const cuiMismatch =
    sessionCui !== "" && formCui !== "" && sessionCui !== formCui;

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleFieldChange = useCallback(
    (field: keyof FormState, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      clearFieldError(field);
    },
    [clearFieldError]
  );

  const handleSectorsChange = useCallback(
    (sectors: string[]) => {
      setForm((prev) => ({ ...prev, sectors }));
      clearFieldError("sectors");
    },
    [clearFieldError]
  );

  const handleUpload = useCallback(
    async (files: File[]) => {
      setIsExtracting(true);
      try {
        const result = await extractDataFromFiles({
          files,
          documentType: "notification_decision",
        });
        const decision = result.mergedData.notificationDecision;
        if (decision && currentSessionId) {
          const input = mapDecisionToInput(currentSessionId, decision);
          setForm(toFormState(input));
          setFieldErrors({});
        } else if (!result.isValid) {
          toast({
            title: "Eroare la extragere",
            description:
              "Nu s-au putut extrage datele din document. Completează manual.",
            variant: "destructive",
          });
        }
      } finally {
        setIsExtracting(false);
      }
    },
    [currentSessionId, toast]
  );

  const handleSave = useCallback(async () => {
    if (!currentSessionId) return;
    const input = toDecizieInput(currentSessionId, form);
    const { isValid, fieldErrors: errors } = validateDecizieFields(input);
    if (!isValid) {
      setFieldErrors(errors);
      const count = Object.keys(errors).length;
      toast({
        title: `${count} ${count === 1 ? "eroare găsită" : "erori găsite"}`,
        description: "Verifică câmpurile marcate.",
        variant: "destructive",
      });
      return;
    }

    if (!creditCheck?.already_purchased) {
      if (!user || (creditCheck && !creditCheck.has_credits)) {
        setShowPaywall(true);
        return;
      }
      try {
        await deductCredits({
          action_type: CREDIT_ACTION_TYPES.EVIDENTA_DECIZIE_INREGISTRARE_DNSC,
          session_id: currentSessionId,
        });
      } catch (errorResult) {
        if (errorResult instanceof InsufficientCreditsError) {
          setShowPaywall(true);
          return;
        }
        toast({
          title: "Eroare",
          description: "Nu s-au putut deduce creditele.",
          variant: "destructive",
        });
        return;
      }
    }

    saveMutation.mutate(input, {
      onSuccess: () => {
        toast({ title: "Decizie salvată cu succes" });
        navigate("/dashboard");
      },
      onError: () => {
        toast({
          title: "Eroare la salvare",
          description: "Încearcă din nou.",
          variant: "destructive",
        });
      },
    });
  }, [
    currentSessionId,
    form,
    saveMutation,
    toast,
    creditCheck,
    deductCredits,
    navigate,
    user,
  ]);

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!notificareCompleted) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">
            Secțiune indisponibilă
          </h2>
          <p className="text-sm text-slate-500 max-w-sm">
            Decizia de înregistrare devine disponibilă după finalizarea
            Formularului de Notificare.
          </p>
          <Button
            onClick={() => navigate("/onboarding/formular-notificare")}
            className="mt-2"
          >
            Completează Formularul de Notificare
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <FileCheck className="w-6 h-6 text-blue-600" />
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900">
            Decizie de Înregistrare
          </h1>
          <p className="text-sm text-slate-500">
            Încarcă decizia primită de la DNSC sau completează manual.
          </p>
        </div>
      </div>

      <FileUploadSection
        onUpload={handleUpload}
        isExtracting={isExtracting}
        accept=".pdf"
      />

      {cuiMismatch && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">CUI nepotrivit</p>
            <p className="text-sm text-amber-700 mt-1">
              CUI-ul din document ({formCui}) nu corespunde cu CUI-ul companiei
              curente ({sessionCui}). Verifică documentul încărcat.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label htmlFor="entity_name">Nume entitate</Label>
          <Input
            id="entity_name"
            value={form.entity_name}
            onChange={(e) => handleFieldChange("entity_name", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cui">CUI</Label>
            <Input
              id="cui"
              value={form.cui}
              onChange={(e) => handleFieldChange("cui", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="category">Categorie</Label>
            <Input
              id="category"
              value={form.category}
              onChange={(e) => handleFieldChange("category", e.target.value)}
              className={cn(fieldErrors["category"] && "border-destructive")}
            />
            <FieldError fieldName="category" errors={fieldErrors} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="decision_number">Număr decizie</Label>
            <Input
              id="decision_number"
              value={form.decision_number}
              onChange={(e) =>
                handleFieldChange("decision_number", e.target.value)
              }
              className={cn(
                fieldErrors["decision_number"] && "border-destructive"
              )}
            />
            <FieldError fieldName="decision_number" errors={fieldErrors} />
          </div>
          <div>
            <Label htmlFor="registration_number">Număr înregistrare</Label>
            <Input
              id="registration_number"
              value={form.registration_number}
              onChange={(e) =>
                handleFieldChange("registration_number", e.target.value)
              }
              className={cn(
                fieldErrors["registration_number"] && "border-destructive"
              )}
            />
            <FieldError fieldName="registration_number" errors={fieldErrors} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="decision_date">Data deciziei</Label>
            <Input
              id="decision_date"
              value={form.decision_date}
              onChange={(e) =>
                handleFieldChange("decision_date", e.target.value)
              }
              className={cn(
                fieldErrors["decision_date"] && "border-destructive"
              )}
            />
            <FieldError fieldName="decision_date" errors={fieldErrors} />
          </div>
          <div>
            <Label htmlFor="county">Județ</Label>
            <Input
              id="county"
              value={form.county}
              onChange={(e) => handleFieldChange("county", e.target.value)}
              className={cn(fieldErrors["county"] && "border-destructive")}
            />
            <FieldError fieldName="county" errors={fieldErrors} />
          </div>
        </div>

        <div>
          <Label htmlFor="address">Adresă</Label>
          <Input
            id="address"
            value={form.address}
            onChange={(e) => handleFieldChange("address", e.target.value)}
            className={cn(fieldErrors["address"] && "border-destructive")}
          />
          <FieldError fieldName="address" errors={fieldErrors} />
        </div>

        <div>
          <Label>Sectoare</Label>
          <SectorPicker
            items={form.sectors}
            onItemsChange={handleSectorsChange}
          />
          <FieldError fieldName="sectors" errors={fieldErrors} />
        </div>
      </div>

      <OnboardingFooter>
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending || cuiMismatch}
          className="min-w-[200px]"
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Se salvează...
            </>
          ) : (
            "Salvează decizia"
          )}
        </Button>
      </OnboardingFooter>

      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={CREDIT_ACTION_TYPES.EVIDENTA_DECIZIE_INREGISTRARE_DNSC}
      />
    </div>
  );
}
